// File: frontend/src/utils/gsapCore.js
//
// The ONE place GSAP is imported and its plugins are registered.
//
// ── WHERE GSAP RUNS ─────────────────────────────────────────────────────────
//
// GSAP drives the public marketing pages: "/" (since 17 Sep 2026) and
// /ambassador. Both routes are lazy() in App.js, and this file is imported only
// by marketing components, so GSAP lands in the marketing chunks and never in
// the login page or the authenticated dashboard.
//
// Framer Motion remains only INSIDE the small marketing chart visuals
// (components/marketing/visuals). No element is animated by both libraries;
// GSAP targets the section wrappers, Framer the chart internals. Those visuals
// are the remaining consolidation candidate.
//
// ── REGISTER ONCE ───────────────────────────────────────────────────────────
//
// registerPlugin is idempotent, but calling it per-component is how a project
// ends up with inconsistent setups. Everything imports gsap THROUGH here.

import { gsap } from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(useGSAP, ScrollTrigger);

export { gsap, useGSAP, ScrollTrigger };

// ── MOTION TOKENS ───────────────────────────────────────────────────────────
//
// Durations and eases live here for the same reason the tier colours live in
// partnerShared.js: per-component invention is how a page ends up feeling like
// six pages. These intentionally echo the Framer variants in marketing/motion.js
// (0.55s, a decelerating ease) so the two public pages read as one product.

export const DUR = {
  fast: 0.25,
  base: 0.55,
  slow: 0.8,
  hero: 1.0,
};

export const EASE = {
  // Entrances decelerate.
  out: "power3.out",
  // Exits accelerate.
  in: "power2.in",
  inOut: "power2.inOut",
  // Scrubbed motion is linear, always — the scrollbar supplies the easing.
  scrub: "none",
};

export const STAGGER = {
  tight: 0.05,
  base: 0.08,
  loose: 0.14,
};

/**
 * Media queries used by every gsap.matchMedia() context on the Partners page.
 *
 * `reduced` is a first-class condition rather than an afterthought: matchMedia
 * REVERTS everything a context created when its query stops matching, so a user
 * toggling reduced motion at the OS level gets the end state rather than a
 * frozen half-animation. A manual window.matchMedia check cannot do that.
 */
export const MQ = {
  reduced: "(prefers-reduced-motion: reduce)",
  isMobile: "(max-width: 899px)",
  isDesktop: "(min-width: 900px)",

  // ── ALWAYS INCLUDE THIS ONE ───────────────────────────────────────────────
  //
  // gsap.matchMedia().add(conditions, fn) runs `fn` only while AT LEAST ONE
  // condition matches. A condition set of just { reduced } therefore runs the
  // callback ONLY for reduced-motion users — the exact inverse of the intent,
  // and it fails silently: the page simply never animates for anybody else,
  // which looks like "the animation isn't working" rather than like a bug in
  // the media query.
  //
  // `always` matches unconditionally, so the callback runs for every visitor
  // and `ctx.conditions.reduced` is what actually decides. Flipping the OS
  // setting still reverts and rebuilds the context, which is the whole reason
  // for using matchMedia over a manual window.matchMedia read.
  always: "(min-width: 0px)",
};

/**
 * Is the browser capable enough for ambient (always-running) motion?
 *
 * The Partners page targets low-end Android. Two signals are cheap and honest:
 * a small logical core count, and the Save-Data header. Neither is a promise —
 * they are a floor that keeps a 4-core phone from running a continuous
 * requestAnimationFrame loop behind the copy.
 *
 * Ambient motion is decorative by definition, so refusing it costs nothing.
 */
export function prefersLightweightMotion() {
  if (typeof window === "undefined" || typeof navigator === "undefined") return true;
  if (navigator.connection?.saveData) return true;
  const cores = Number(navigator.hardwareConcurrency);
  return Number.isFinite(cores) && cores > 0 && cores <= 4;
}
